import { useMemo } from 'react';

import { useAppData } from '@/app-data';
import { useIdentity } from '@/features/identity/identity';
import { Member } from '@/features/backlog/types';
import MemberAvatar from '@/components/member-avatar';
import { Popover } from '@/components/ui/Popover';
import { cn } from '@/components/ui/cn';
import { useRealtime } from './realtime';

const MAX_SHOWN = 3;

/** Overlapping, stacked avatars of the OTHER people currently on this board (you're in the
 *  account chip already). Beyond MAX_SHOWN the surplus shows as a +N chip. Clicking anywhere
 *  on the stack opens a flyout roster of everyone active. Hidden when you're alone. Live via
 *  presence.sync. */
export default function PresenceBar() {
  const { presence } = useRealtime();
  const { members } = useAppData();
  const { me } = useIdentity();

  const others = useMemo(() => {
    const byId = new Map(members.map((m) => [m.id, m]));
    return presence
      .filter((id) => id !== me?.id)
      .map((id) => byId.get(id))
      .filter((m): m is Member => !!m);
  }, [presence, members, me?.id]);

  // Nothing to show when you're alone — render NOTHING (not even the flanking separators, which
  // otherwise leave a superfluous "| |" gap in the header).
  if (others.length === 0) return null;
  const shown = others.slice(0, MAX_SHOWN);
  const extra = others.length - shown.length;
  const activeCount = others.length + (me ? 1 : 0);

  return (
    // Desktop-only; carries its own separators so they appear only alongside the avatars.
    <span className="hidden items-center gap-1 sm:flex">
      <span className="mx-1 h-5 w-px bg-line" />
    <Popover
      align="right"
      panelClassName="min-w-[240px]"
      trigger={({ toggle, open }) => (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-label={`${activeCount} people online — show everyone`}
          className={cn(
            'flex items-center rounded-full p-1 transition-colors hover:bg-layer',
            open && 'bg-layer',
          )}
        >
          {/* A ring in the header colour cuts each avatar out of the one behind it, so the row
              reads as one stack. The ring goes straight on the (fixed-size) avatar — a wrapper
              span's taller line-box would round the ring into an oval with a gap. Clicking
              anywhere opens the roster. */}
          <span className="flex items-center -space-x-[7px]">
            {shown.map((m) => (
              <MemberAvatar key={m.id} member={m} size={26} ring={false} className="ring-2 ring-ink" />
            ))}
            {extra > 0 ? (
              <span className="flex h-[26px] min-w-[26px] items-center justify-center rounded-full bg-[#71717a] px-1.5 text-[11px] font-semibold text-white ring-2 ring-ink">
                +{extra}
              </span>
            ) : null}
          </span>
        </button>
      )}
    >
      {() => <PresenceRoster others={others} me={me} />}
    </Popover>
      <span className="mx-1 h-5 w-px bg-line" />
    </span>
  );
}

/** The flyout behind the stack: everyone active on this board right now — you first,
 *  then the others — each with a live "online" dot. */
function PresenceRoster({ others, me }: { others: Member[]; me?: Member | null }) {
  const count = others.length + (me ? 1 : 0);
  return (
    <div className="min-w-[220px]">
      <div className="px-2 pb-1 pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-fg-faint">
        {count} online
      </div>
      <div className="mb-1 h-px bg-line" />
      <ul>
        {me ? <RosterRow member={me} you /> : null}
        {others.map((m) => (
          <RosterRow key={m.id} member={m} />
        ))}
      </ul>
    </div>
  );
}

function RosterRow({ member, you = false }: { member: Member; you?: boolean }) {
  return (
    <li className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
      <MemberAvatar member={member} size={28} />
      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-fg-soft">
        {member.name}
        {you ? <span className="ml-1.5 text-[11px] font-normal text-fg-faint">You</span> : null}
      </span>
      <span className="h-2 w-2 shrink-0 rounded-full bg-ok" title="Online" aria-label="Online" />
    </li>
  );
}
